import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useScene } from "./SceneController";

const COLORS = [
  "#3b82f6",
  "#22c55e",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];

const START_POINTS = [
  [-4, 2, -1],
  [-4, -1.5, 0],
  [-3.5, 0.8, 1],
  [-4.5, -2, -0.5],
  [-3.8, 1.8, 0.6],
];

const PATH = [
  new THREE.Vector3(-3.2, 0, 0),
  new THREE.Vector3(-1.5, 0, 0),
  new THREE.Vector3(0, 0, 0),
  new THREE.Vector3(2, 0, 0),
  new THREE.Vector3(4, 0, 0),
];

function lerpPath(progress) {
  const total = PATH.length - 1;

  const scaled = progress * total;

  const index = Math.floor(scaled);

  const next = Math.min(index + 1, total);

  const alpha = scaled - index;

  return new THREE.Vector3().lerpVectors(
    PATH[index],
    PATH[next],
    alpha
  );
}

export default function FloatingDocuments() {
  const group = useRef();

  const { sceneState } = useScene();

  const docs = useMemo(() => {
    return Array.from({ length: 16 }, (_, i) => ({
      id: i,
      speed: 0.08 + Math.random() * 0.05,
      progress: Math.random(),
      delay: Math.random(),
      color: COLORS[i % COLORS.length],
      offset:
        START_POINTS[i % START_POINTS.length],
    }));
  }, []);

  useFrame((state, delta) => {
    if (!group.current) return;

    group.current.children.forEach((mesh, i) => {
      const d = docs[i];

      d.progress += delta * d.speed;

      if (d.progress > 1) {
        d.progress = 0;
      }

      const p = lerpPath(d.progress);

      if (sceneState.section === "problem") {
        p.x += Math.sin(state.clock.elapsedTime * 5 + i) * 0.4;
        p.y += Math.cos(state.clock.elapsedTime * 7 + i) * 0.4;
      }

      if (sceneState.section === "hero") {
        p.y += Math.sin(state.clock.elapsedTime * 2 + i) * 0.25;
      }

      if (sceneState.section === "vision") {
        p.y += Math.sin(state.clock.elapsedTime * 4 + i) * 0.05;
      }

      mesh.position.lerp(p, 0.08);

      mesh.rotation.y += delta;

      mesh.rotation.z =
        Math.sin(state.clock.elapsedTime * 3 + i) * 0.25;

      const opacity =
        d.progress < 0.08
          ? d.progress / 0.08
          : d.progress > 0.92
          ? (1 - d.progress) / 0.08
          : 1;

      mesh.children.forEach((child) => {
        if (child.material) {
          child.material.transparent = true;
          child.material.opacity = opacity;
        }
      });
    });
  });

  return (
    <group ref={group}>
      {docs.map((doc) => (
        <group key={doc.id}>

          <mesh>
            <boxGeometry args={[0.35, 0.45, 0.03]} />

            <meshPhysicalMaterial
              color="white"
              roughness={0.2}
              metalness={0.15}
              clearcoat={1}
            />
          </mesh>

          <mesh position={[0, 0.12, 0.02]}>
            <boxGeometry args={[0.18, 0.025, 0.005]} />

            <meshBasicMaterial color={doc.color} />
          </mesh>

          {[0,1,2].map((i)=>(

            <mesh
              key={i}
              position={[0,-0.05-i*0.08,0.02]}
            >

              <boxGeometry args={[0.18,0.012,0.004]} />

              <meshBasicMaterial color="#94a3b8"/>

            </mesh>

          ))}

        </group>
      ))}
    </group>
  );
}